import {HTMLProps, useState} from 'react'
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {AnyObjectSchema} from 'yup'
import useDeepCompareEffect from 'use-deep-compare-effect'

// @ts-ignore # for using function instead of MouseEventHandler as onClick
interface Props extends HTMLProps<HTMLFormElement> {
  onSubmit?: SubmitHandler<{[x: string]: any}>
  defaultValues?: any
  schema: AnyObjectSchema
  onChange?: SubmitHandler<{[x: string]: any}>
  /**
   * Trigger of onSubmit function call on every input change
   */
  submitOnChange?: boolean
  /**
   * Submit by the enter key to submit the form default:true
   */
  submitByEnter?: boolean
  resetOnSubmit?: boolean
}

export const Form = ({
  className,
  defaultValues = {},
  children,
  schema,
  onSubmit = () => null,
  onChange,
  submitOnChange = false,
  submitByEnter = true,
  resetOnSubmit = false,
}: Props) => {
  const [initialized, setInitialized] = useState(false)
  const [loading, setLoading] = useState(false)

  const methods = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues,
    reValidateMode: 'onChange',
  })

  useDeepCompareEffect(() => {
    if (!initialized) {
      setInitialized(true)
    } else if (defaultValues) {
      setInitialized(true)
      methods.reset(defaultValues)
    }
  }, [defaultValues])

  const handleSubmit = async (data: any) => {
    try {
      setLoading(true)
      await onSubmit(data)
      if (resetOnSubmit) {
        methods.reset(defaultValues)
      }
    } catch (err: any) {
      if (err.body)
        Object.keys(err.body).forEach(name => {
          const messages = err.body[name]
          const _messages = messages?.non_field_errors || messages
          methods.setError(name, {
            type: 'manual',
            message:
              typeof _messages[0] === 'string' ? _messages[0] : undefined,
            // body: messages as any,
          })
        })
    }
    setLoading(false)
  }

  const handleFormChange = () => {
    const watch = {...methods.watch()}
    if (onChange) onChange(watch)
    if (submitOnChange) {
      methods.handleSubmit(handleSubmit)()
    }
  }

  return (
    <FormProvider
      {...methods}
      // @ts-ignore
      submitOnChange={submitOnChange}
      defaultValues={defaultValues}
      handleFormChange={handleFormChange}>
      <form
        onKeyDown={e =>
          !submitByEnter && e.key === 'Enter' && e.preventDefault()
        }
        className={`${className} ${loading ? 'loading' : ''}`}
        onSubmit={methods.handleSubmit(handleSubmit)}
        onChange={handleFormChange}>
        {children}
      </form>
    </FormProvider>
  )
}
